// Kho dữ liệu module Tài liệu dùng chung giữa các màn (mock, lưu localStorage — sẽ thay bằng API khi có backend)
import dayjs from 'dayjs';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import {
  EMPLOYEES, TRASH_DAYS, meEmployee, seedCategories, seedDocs, seedTypes,
  type AccessAction, type Doc, type DocCategory, type DocType,
} from './data';
import { descendantIds, recipientIds } from './logic';

export const DOCS_STORAGE_KEY = 'khothi-docs-v1';

export type DocRole = 'staff' | 'manager' | 'admin';
export interface Actor { id: string; name: string }

export type DocInput = Pick<Doc,
  'code' | 'number' | 'title' | 'summary' | 'typeId' | 'categoryId' | 'blockId' | 'unitId' | 'issuerId' | 'signerId' |
  'effectiveFrom' | 'effectiveTo' | 'draftMode' | 'shareAll' | 'internalShare' | 'sharedUserIds' | 'emailNotify' | 'replacesId' | 'files'>;

export type SaveAction = 'draft' | 'submit' | 'publish' | 'share';

interface DocsState {
  types: DocType[];
  categories: DocCategory[];
  docs: Doc[];
  /** Nhãn cá nhân của người dùng hiện tại: docId → nhãn */
  myLabels: Record<string, string[]>;
  labelList: string[];
  demoRole: DocRole | null;
  setDemoRole: (r: DocRole | null) => void;

  saveType: (input: Pick<DocType, 'code' | 'name' | 'prefix' | 'active'> & { id?: string }) => DocType;
  deleteType: (id: string) => void;
  reorderTypes: (ids: string[]) => void;

  saveCategory: (input: Pick<DocCategory, 'code' | 'name' | 'typeId' | 'parentId' | 'active' | 'visible'> & { id?: string }) => DocCategory;
  deleteCategory: (id: string) => void;
  reorderCategories: (ids: string[]) => void;

  saveDoc: (input: DocInput, opts: { id?: string; action: SaveAction; by: Actor }) => Doc;
  submitDoc: (id: string, by: Actor) => void;
  withdrawDoc: (id: string, by: Actor) => void;
  approveDoc: (id: string, by: Actor) => void;
  rejectDoc: (id: string, reason: string, by: Actor) => void;
  revokeDoc: (id: string, reason: string, by: Actor) => void;
  deleteDoc: (id: string, by: Actor) => void;
  restoreDoc: (id: string, by: Actor) => void;
  /** Xóa vĩnh viễn — trả về id file để xóa nội dung trong IndexedDB */
  purgeDoc: (id: string) => string[];
  purgeExpiredTrash: () => string[];
  markViewed: (id: string, by: Actor) => void;
  logAccess: (id: string, by: Actor, action: AccessAction, fileName?: string) => void;
  addComment: (id: string, by: Actor, content: string) => void;
  /** Rà soát định kỳ: xác nhận tài liệu vẫn phù hợp, giữ nguyên nội dung */
  reviewDoc: (id: string, by: Actor, note?: string) => void;
  /** Nhắc người được chia sẻ chưa đọc (mock: ghi lịch sử) */
  remindReaders: (id: string, by: Actor, count: number) => void;
  setLabels: (docId: string, labels: string[]) => void;
  deleteLabel: (label: string) => void;
  resetDemo: () => void;
}

const nowIso = () => new Date().toISOString();
const uid = (p: string) => `${p}-${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;

function buildSeed() {
  const types = seedTypes();
  const categories = seedCategories();
  return { types, categories, docs: seedDocs(types, categories), myLabels: {} as Record<string, string[]>, labelList: ['Quan trọng', 'Cần đọc lại'] };
}

/** Áp dụng ban hành: thay thế bản cũ (nếu ban hành lại) + ghi nhận gửi email thông báo */
function applyPublish(docs: Doc[], id: string, by: Actor): Doc[] {
  const at = nowIso();
  const target = docs.find(d => d.id === id)!;
  const people = [...EMPLOYEES, meEmployee(by.name)];
  return docs.map(d => {
    if (d.id === id) {
      const history = [...d.history, { at, by: by.name, action: 'Duyệt & ban hành' }];
      if (d.emailNotify) history.push({ at, by: 'Hệ thống', action: `Gửi email thông báo tới ${recipientIds(d, people).length} người` });
      return { ...d, status: 'published', approvedBy: by.name, approvedAt: at, rejectReason: undefined, updatedAt: at, history };
    }
    if (target.replacesId && d.id === target.replacesId) {
      const dayBefore = dayjs(target.effectiveFrom).subtract(1, 'day').format('YYYY-MM-DD');
      const effectiveTo = !d.effectiveTo || d.effectiveTo > dayBefore ? (dayBefore < d.effectiveFrom ? d.effectiveFrom : dayBefore) : d.effectiveTo;
      return { ...d, replacedById: id, effectiveTo, updatedAt: at, history: [...d.history, { at, by: by.name, action: `Bị thay thế bởi ${target.code}` }] };
    }
    return d;
  });
}

const useDocsStore = create<DocsState>()(
  persist(
    (set, get) => ({
      ...buildSeed(),
      demoRole: null,
      setDemoRole: demoRole => set({ demoRole }),

      saveType: input => {
        const { types } = get();
        if (input.id) {
          const updated = { ...types.find(t => t.id === input.id)!, ...input } as DocType;
          set({ types: types.map(t => (t.id === input.id ? updated : t)) });
          return updated;
        }
        const created: DocType = { ...input, id: uid('t'), order: types.length, createdAt: nowIso() };
        set({ types: [...types, created] });
        return created;
      },
      deleteType: id => set(s => ({ types: s.types.filter(t => t.id !== id) })),
      reorderTypes: ids => set(s => ({ types: s.types.map(t => ({ ...t, order: ids.indexOf(t.id) >= 0 ? ids.indexOf(t.id) : t.order })) })),

      saveCategory: input => {
        const { categories } = get();
        if (input.id) {
          const prev = categories.find(c => c.id === input.id)!;
          const updated = { ...prev, ...input } as DocCategory;
          // Đổi cha → đưa xuống cuối nhóm mới; đổi loại ở node cha → các node con theo cùng loại
          if (prev.parentId !== input.parentId) updated.order = categories.filter(c => c.parentId === input.parentId).length;
          const children = new Set(descendantIds(categories, input.id));
          set({
            categories: categories.map(c => (c.id === input.id ? updated : children.has(c.id) ? { ...c, typeId: updated.typeId } : c)),
          });
          return updated;
        }
        const created: DocCategory = {
          ...input, id: uid('c'), order: categories.filter(c => c.parentId === input.parentId).length, createdAt: nowIso(),
        };
        set({ categories: [...categories, created] });
        return created;
      },
      deleteCategory: id => set(s => ({ categories: s.categories.filter(c => c.id !== id) })),
      reorderCategories: ids => set(s => ({ categories: s.categories.map(c => (ids.includes(c.id) ? { ...c, order: ids.indexOf(c.id) } : c)) })),

      saveDoc: (input, { id, action, by }) => {
        const at = nowIso();
        const { docs } = get();
        const prev = id ? docs.find(d => d.id === id) : undefined;
        let doc: Doc;
        if (prev && action === 'share') {
          doc = {
            ...prev,
            shareAll: input.shareAll, internalShare: input.internalShare, sharedUserIds: input.sharedUserIds, emailNotify: input.emailNotify,
            updatedAt: at, history: [...prev.history, { at, by: by.name, action: 'Cập nhật phân quyền chia sẻ' }],
          };
        } else {
          const status = action === 'draft' ? 'draft' : action === 'submit' ? 'pending' : prev?.status === 'published' ? 'published' : 'pending';
          const history = prev ? [...prev.history, { at, by: by.name, action: 'Cập nhật nội dung' }] : [{ at, by: by.name, action: 'Tạo tài liệu' }];
          if (action === 'submit') history.push({ at, by: by.name, action: 'Gửi duyệt' });
          doc = {
            ...(prev || {
              id: uid('doc'), createdBy: by.id, createdByName: by.name, createdAt: at, replacedById: null, deletedAt: null,
              views: {}, comments: [], access: [],
            }),
            ...input,
            status,
            submittedAt: action === 'draft' ? prev?.submittedAt : at,
            rejectReason: action === 'draft' ? prev?.rejectReason : undefined,
            updatedAt: at,
            history,
          } as Doc;
        }
        let next = prev ? docs.map(d => (d.id === doc.id ? doc : d)) : [doc, ...docs];
        if (action === 'publish') next = applyPublish(next, doc.id, by);
        set({ docs: next });
        return next.find(d => d.id === doc.id)!;
      },
      submitDoc: (id, by) => set(s => ({
        docs: s.docs.map(d => (d.id === id ? { ...d, status: 'pending', submittedAt: nowIso(), rejectReason: undefined, history: [...d.history, { at: nowIso(), by: by.name, action: 'Gửi duyệt' }] } : d)),
      })),
      withdrawDoc: (id, by) => set(s => ({
        docs: s.docs.map(d => (d.id === id ? { ...d, status: 'draft', history: [...d.history, { at: nowIso(), by: by.name, action: 'Rút lại về dự thảo' }] } : d)),
      })),
      approveDoc: (id, by) => set(s => ({ docs: applyPublish(s.docs, id, by) })),
      rejectDoc: (id, reason, by) => set(s => ({
        docs: s.docs.map(d => (d.id === id ? { ...d, status: 'rejected', rejectReason: reason, updatedAt: nowIso(), history: [...d.history, { at: nowIso(), by: by.name, action: 'Từ chối', note: reason }] } : d)),
      })),
      revokeDoc: (id, reason, by) => set(s => ({
        docs: s.docs.map(d => (d.id === id ? { ...d, status: 'revoked', revokeReason: reason, revokedAt: nowIso(), updatedAt: nowIso(), history: [...d.history, { at: nowIso(), by: by.name, action: 'Thu hồi', note: reason }] } : d)),
      })),
      deleteDoc: (id, by) => set(s => ({
        docs: s.docs.map(d => (d.id === id ? { ...d, deletedAt: nowIso(), deletedBy: by.name, history: [...d.history, { at: nowIso(), by: by.name, action: 'Chuyển vào thùng rác' }] } : d)),
      })),
      restoreDoc: (id, by) => set(s => ({
        docs: s.docs.map(d => (d.id === id ? { ...d, deletedAt: null, deletedBy: null, history: [...d.history, { at: nowIso(), by: by.name, action: 'Khôi phục từ thùng rác' }] } : d)),
      })),
      purgeDoc: id => {
        const doc = get().docs.find(d => d.id === id);
        set(s => ({
          // Gỡ liên kết ban hành lại để bản còn lại không trỏ tới tài liệu đã xóa
          docs: s.docs.filter(d => d.id !== id).map(d => (d.replacesId === id ? { ...d, replacesId: null } : d.replacedById === id ? { ...d, replacedById: null } : d)),
          myLabels: Object.fromEntries(Object.entries(s.myLabels).filter(([k]) => k !== id)),
        }));
        return doc ? doc.files.filter(f => !f.demo).map(f => f.id) : [];
      },
      purgeExpiredTrash: () => {
        const limit = dayjs().subtract(TRASH_DAYS, 'day').toISOString();
        const expired = get().docs.filter(d => d.deletedAt && d.deletedAt < limit);
        return expired.flatMap(d => get().purgeDoc(d.id));
      },
      markViewed: (id, by) => set(s => ({
        docs: s.docs.map(d => {
          if (d.id !== id) return d;
          const at = nowIso();
          return { ...d, views: d.views[by.id] ? d.views : { ...d.views, [by.id]: at }, access: [...d.access, { userId: by.id, userName: by.name, action: 'view', at }].slice(-200) };
        }),
      })),
      logAccess: (id, by, action, fileName) => set(s => ({
        docs: s.docs.map(d => (d.id === id ? { ...d, access: [...d.access, { userId: by.id, userName: by.name, action, at: nowIso(), fileName }].slice(-200) } : d)),
      })),
      addComment: (id, by, content) => set(s => ({
        docs: s.docs.map(d => (d.id === id ? { ...d, comments: [...d.comments, { id: uid('cm'), userId: by.id, userName: by.name, content, at: nowIso() }] } : d)),
      })),
      reviewDoc: (id, by, note) => set(s => ({
        docs: s.docs.map(d => (d.id === id ? { ...d, reviewedAt: nowIso(), reviewedBy: by.name, history: [...d.history, { at: nowIso(), by: by.name, action: 'Rà soát định kỳ — giữ nguyên', note }] } : d)),
      })),
      remindReaders: (id, by, count) => set(s => ({
        docs: s.docs.map(d => (d.id === id ? { ...d, history: [...d.history, { at: nowIso(), by: by.name, action: `Nhắc đọc ${count} người chưa đọc` }] } : d)),
      })),
      setLabels: (docId, labels) => set(s => ({
        myLabels: { ...s.myLabels, [docId]: labels },
        labelList: Array.from(new Set([...s.labelList, ...labels])),
      })),
      deleteLabel: label => set(s => ({
        labelList: s.labelList.filter(l => l !== label),
        myLabels: Object.fromEntries(Object.entries(s.myLabels).map(([k, v]) => [k, v.filter(l => l !== label)])),
      })),
      resetDemo: () => set({ ...buildSeed() }),
    }),
    {
      name: DOCS_STORAGE_KEY,
      // v3: dữ liệu mẫu có thời gian duyệt, vòng từ chối, lượt đọc/tải theo người nhận, lịch hết hiệu lực → nạp lại dữ liệu mẫu
      version: 3,
      migrate: (persisted, version) => {
        const prev = persisted as Partial<DocsState>;
        return (version < 3 ? { ...prev, ...buildSeed() } : prev) as DocsState;
      },
    },
  ),
);

// Đồng bộ giữa các tab trình duyệt. Bỏ qua dữ liệu do tab chạy phiên bản code khác ghi (vd tab cũ chưa tải lại sau khi
// cập nhật) — nếu không, migrate sẽ coi đó là dữ liệu cũ và nạp lại dữ liệu mẫu, làm mất thay đổi của người dùng.
if (typeof window !== 'undefined') {
  window.addEventListener('storage', e => {
    if (e.key !== DOCS_STORAGE_KEY || !e.newValue) return;
    try {
      if (JSON.parse(e.newValue).version !== useDocsStore.persist.getOptions().version) return;
    } catch {
      return;
    }
    useDocsStore.persist.rehydrate();
  });
}

export default useDocsStore;
